"use client";

import { useMemo, useRef, useState } from "react";
import { Check, Loader2, Search } from "lucide-react";
import {
  CATALOG_PICKER_SOURCES,
  catalogUseLabel,
  translatedCatalogLabel,
} from "@repo/questionnaire";
import { toast } from "sonner";
import { CommandEmpty, CommandItem } from "@/components/ui/command";
import { SearchableComboBox } from "@/components/ui/searchable-combo-box";
import { api } from "@/lib/api-client";
import { cn } from "@/lib/utils";

export type BusinessTypeOption = {
  id: number;
  name: string;
  displayName?: string;
  slug?: string | null;
  categoryName?: string;
  categoryDisplayName?: string;
  iconName?: string | null;
};

type BusinessTypeComboboxBaseProps = {
  dealId?: string;
  /** When set (e.g. public intake), enables create-if-needed without a deal. */
  onResolveCreate?: (
    description: string,
  ) => Promise<BusinessTypeOption> | BusinessTypeOption;
  /**
   * Saves the typed words immediately, before catalog resolution finishes.
   * Without `dealId`, single mode treats typing as answering: no "Use …" step.
   */
  onUnresolvedText?: (description: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  inputContainerClassName?: string;
  triggerClassName?: string;
  onCreated?: (businessType: BusinessTypeOption) => void | Promise<void>;
  /** Hash-keyed translations for the form language. English stays the stored name. */
  translations?: Record<string, string>;
  /** Locale used to load entity-linked catalog display names. */
  locale?: string;
};

type BusinessTypeComboboxSingleProps = BusinessTypeComboboxBaseProps & {
  mode?: "single";
  value?: BusinessTypeOption | null;
  onChange: (businessType: BusinessTypeOption) => void | Promise<void>;
  values?: never;
  onValuesChange?: never;
};

type BusinessTypeComboboxMultiProps = BusinessTypeComboboxBaseProps & {
  mode: "multi";
  values: BusinessTypeOption[];
  onValuesChange: (values: BusinessTypeOption[]) => void;
  value?: never;
  onChange?: never;
};

export type BusinessTypeComboboxProps =
  | BusinessTypeComboboxSingleProps
  | BusinessTypeComboboxMultiProps;

export function BusinessTypeCombobox(props: BusinessTypeComboboxProps) {
  const {
    dealId,
    onResolveCreate,
    onUnresolvedText,
    disabled,
    autoFocus,
    placeholder = "Search business type...",
    className,
    inputContainerClassName,
    triggerClassName,
    onCreated,
    translations,
    locale = "en",
  } = props;
  const staticLabel = (name: string) => translatedCatalogLabel(name, translations);
  const entityLabel = (item: BusinessTypeOption) =>
    item.displayName?.trim() || item.name;
  const categoryLabel = (item: BusinessTypeOption) =>
    item.categoryDisplayName?.trim() || item.categoryName;
  const searchLabel = (item: BusinessTypeOption) =>
    [item.name, item.displayName, item.categoryName, item.categoryDisplayName]
      .filter((value): value is string => Boolean(value))
      .flatMap((value) => [value, normalizeSearchText(value)])
      .join(" ");

  const isMulti = props.mode === "multi";
  const multiValues = isMulti ? props.values : undefined;
  const singleValue = !isMulti ? props.value : undefined;
  const selected = useMemo(
    () => (isMulti ? (multiValues ?? []) : singleValue ? [singleValue] : []),
    [isMulti, multiValues, singleValue],
  );
  const typeToAnswer = !isMulti && !dealId && Boolean(onUnresolvedText);
  const [draftText, setDraftText] = useState<string | null>(null);
  const comboSelected = useMemo(() => {
    if (!typeToAnswer || draftText == null) return selected;
    const draftKey = draftText.toLowerCase();
    return selected.filter(
      (item) => item.id > 0 || item.name.trim().toLowerCase() !== draftKey,
    );
  }, [draftText, selected, typeToAnswer]);

  const categoriesQuery = api.listings.getIndustryCategories.useQuery(
    { locale },
    {
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
  const { data } = categoriesQuery;
  const catalogPending = categoriesQuery.isPending;
  const options = useMemo(
    () =>
      (data?.industryCategories ?? []).flatMap((category) =>
        (category.businessTypes ?? []).map((type) => ({
          id: type.id,
          name: type.name,
          displayName: type.displayName,
          slug: type.slug,
          categoryName: category.name,
          categoryDisplayName: category.displayName,
          iconName: type.iconName,
        })),
      ),
    [data?.industryCategories],
  );

  const inferBusinessType = api.os.deals.inferDealBusinessType.useMutation({
    onSuccess: async (result) => {
      const option = options.find((item) => item.id === result.businessType.id) ?? {
        id: result.businessType.id,
        name: result.businessType.name,
        displayName: result.businessType.name,
        slug: result.businessType.slug,
        iconName: result.businessType.iconName,
      };
      await onCreated?.(option);
      await selectOption(option);
    },
    onError: (error) => toast.error(error.message),
  });

  const catalogFailed = categoriesQuery.isError && options.length === 0;
  const canAcceptCustom = Boolean(
    !catalogPending && (dealId || onResolveCreate || onUnresolvedText),
  );
  const busy = disabled || inferBusinessType.isPending || catalogPending;
  const pendingDescription = useRef<string | null>(null);

  async function selectOption(option: BusinessTypeOption) {
    setDraftText(null);
    if (isMulti) {
      if (props.values.some((item) => item.id === option.id)) {
        props.onValuesChange(
          props.values.filter((item) => item.id !== option.id),
        );
      } else {
        props.onValuesChange([...props.values, option]);
      }
      return;
    }
    await props.onChange(option);
  }

  function removeOption(option: BusinessTypeOption) {
    if (!isMulti) return;
    props.onValuesChange(props.values.filter((item) => item.id !== option.id));
  }

  async function createFromDescription(description: string) {
    const trimmed = description.trim();
    const key = trimmed.toLowerCase();
    if (
      trimmed.length < MIN_TYPED_LENGTH ||
      pendingDescription.current === key
    ) {
      return;
    }
    if (
      selected.some(
        (item) => item.id > 0 && item.name.toLowerCase() === key,
      )
    ) {
      return;
    }
    pendingDescription.current = key;
    onUnresolvedText?.(trimmed);
    if (dealId) {
      inferBusinessType.mutate(
        { dealId, description: trimmed },
        {
          onSettled: () => {
            pendingDescription.current = null;
          },
        },
      );
      return;
    }
    if (!onResolveCreate) {
      pendingDescription.current = null;
      return;
    }
    try {
      const option = await onResolveCreate(trimmed);
      const localizedOption =
        options.find((item) => item.id === option.id) ?? option;
      await onCreated?.(localizedOption);
      await selectOption(localizedOption);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not match that business type.",
      );
    } finally {
      pendingDescription.current = null;
    }
  }

  function commitDraft() {
    if (draftText == null) return;
    const text = draftText;
    setDraftText(null);
    void createFromDescription(text);
  }

  return (
    <div className={cn("w-full", className)}>
      <SearchableComboBox
        type={isMulti ? "multi" : "single"}
        identifier={`business-type-${dealId ?? "global"}-${isMulti ? "multi" : "single"}`}
        items={options}
        selectedItems={comboSelected}
        itemKey={businessTypeKey}
        displayNameGetter={entityLabel}
        autoFocus={autoFocus}
        enterAdvances={typeToAnswer}
        onInputTextChange={
          typeToAnswer
            ? (text) => {
                const trimmed = text.trim();
                setDraftText(trimmed);
                onUnresolvedText?.(
                  trimmed.length >= MIN_TYPED_LENGTH ? trimmed : "",
                );
              }
            : undefined
        }
        disabled={busy}
        placeholder={staticLabel(
          catalogPending
            ? CATALOG_PICKER_SOURCES.loadingBusinessTypes
            : placeholder,
        )}
        nameGetter={searchLabel}
        onItemRemoved={removeOption}
        inputContainerClassName={inputContainerClassName}
        triggerClassName={triggerClassName}
        renderSearchBoxLeading={() =>
          inferBusinessType.isPending || catalogPending ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Search className="h-4 w-4 text-muted-foreground" />
          )
        }
        onDismiss={({ inputText, matchCount }) => {
          if (typeToAnswer) {
            commitDraft();
            return;
          }
          if (matchCount > 0) return;
          void createFromDescription(inputText);
        }}
        onInputKeyDown={(event, { inputText, matchCount }) => {
          if (event.key !== "Enter" || matchCount > 0) return;
          if (typeToAnswer) {
            commitDraft();
            return;
          }
          event.preventDefault();
          void createFromDescription(inputText);
        }}
        renderCommandEmpty={({ inputText }) => {
          if (typeToAnswer) return null;
          const trimmed = inputText.trim();
          if (
            catalogPending ||
            !canAcceptCustom ||
            trimmed.length < MIN_TYPED_LENGTH
          ) {
            const message = catalogPending
              ? CATALOG_PICKER_SOURCES.loadingBusinessTypes
              : catalogFailed
                ? CATALOG_PICKER_SOURCES.businessTypeLoadError
                : CATALOG_PICKER_SOURCES.emptyBusinessType;
            return (
              <CommandEmpty className="py-3 text-center text-sm text-muted-foreground">
                {staticLabel(message)}
              </CommandEmpty>
            );
          }
          return (
            <CommandItem
              value={trimmed}
              disabled={busy}
              onSelect={() => void createFromDescription(trimmed)}
            >
              {catalogUseLabel(trimmed, translations)}
            </CommandItem>
          );
        }}
        renderCommandItem={({ item }) => (
          <CommandItem
            key={item.id}
            value={searchLabel(item)}
            onSelect={() => void selectOption(item)}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {entityLabel(item)}
              </p>
              {categoryLabel(item) ? (
                <p className="truncate text-[11px] text-muted-foreground">
                  {categoryLabel(item)}
                </p>
              ) : null}
            </div>
            <Check
              className={cn(
                "ml-auto h-4 w-4",
                selected.some((s) => s.id === item.id)
                  ? "opacity-100"
                  : "opacity-0",
              )}
            />
          </CommandItem>
        )}
        renderItem={({ item }) => (
          <span className="truncate text-sm">{entityLabel(item)}</span>
        )}
      />
    </div>
  );
}

const MIN_TYPED_LENGTH = 3;

function businessTypeKey(item: BusinessTypeOption): string {
  return item.id > 0
    ? `id:${item.id}`
    : `text:${item.name.trim().toLowerCase()}`;
}

function normalizeSearchText(value: string): string {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}
